import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { useStoreContext } from "@context";

import { useOnlineStatus } from "@shared/hooks";
import {
  SendWebcamScreenshotParams,
  useSendWebcamScreenshot,
} from "@shared/hooks/query/applicants/useApplicantWebcam";

export const webcamQueryKey = (applicantExamId: number) => [
  `${applicantExamId}-webcam-screenshots`,
];

/**
 * This component manages and uploads webcam screenshots to an S3 bucket.
 * It leverages React hooks and the `react-query` library to handle the state and asynchronous operations.
 *
 * Workflow:
 * The component initializes with an empty list of images to upload.
 * When new data is received via useQuery, it is added to the imagesToUpload state.
 *   Data is added via setQueryData implemented on the WebcamEventHandler component.
 * If the user is online and there are images to upload, the first image in the queue is sent to S3 using the sendWebcamScreenshot mutation.
 * After the image is processed, it is removed from the queue, and the state is updated accordingly.
 */
export const WebcamScreenshotUploader = () => {
  const { applicantExamId } = useStoreContext();

  // a string of image data urls
  const [imagesToUpload, setImagesToUpload] = useState<
    SendWebcamScreenshotParams[]
  >([]);
  const [imageToUpload, setImageToUpload] =
    useState<SendWebcamScreenshotParams | null>(null);

  const handleImageProcessDone = () => {
    setImagesToUpload((prevImages) =>
      prevImages.filter((oldImage) => oldImage !== imageToUpload),
    );
    setImageToUpload(null);
  };

  const { mutate: sendWebcamScreenshot } = useSendWebcamScreenshot({
    onSuccess: handleImageProcessDone,
    onError: handleImageProcessDone,
  });

  const isOnline = useOnlineStatus();

  useEffect(() => {
    if (imageToUpload || !imagesToUpload.length || !isOnline) {
      return;
    }

    const img = imagesToUpload[0];
    sendWebcamScreenshot(img);
    setImageToUpload(img);
  }, [imagesToUpload, sendWebcamScreenshot, imageToUpload, isOnline]);

  const { data } = useQuery<SendWebcamScreenshotParams | null>({
    queryKey: webcamQueryKey(applicantExamId),
    queryFn: () => null,
    enabled: false,
    initialData: null,
  });

  useEffect(() => {
    if (data) {
      setImagesToUpload((oldImages) => [...oldImages, data]);
    }
  }, [data]);

  return null;
};
